import type { FitnessDay, FitnessResponse, FormZone, Sport } from '@painpal/contracts';
import { Layers, Pointer } from 'lucide-react';
import { useMemo } from 'react';
import type { SportFilter } from '../../api/keys';
import { EChart } from '../../components/EChart';
import { ToggleTabs } from '../../components/ToggleTabs';
import { FITNESS_RANGES, RANGE_LABEL, type FitnessRange } from '../../lib/dates';
import { formatWeekdayDayMonth } from '../../lib/format';
import { useElementWidth } from '../../ui/useElementWidth';
import { SPORT_COLORS } from '../../theme/tokens';
import { useVariant } from '../../ui/useMediaQuery';
import {
  endLabelFits, FITNESS_CHART_HEIGHT, FITNESS_CHART_HEIGHT_PHONE, fitnessChartOption, loadSports, PHONE_ZONE_KEY_TOP, planEnd, ZONE_BAND,
} from './fitnessChartOption';
import { peakLabel, projectionLine, todayNote } from './fitnessStats';
import { FitnessDataTable } from './FitnessDataTable';
import { ZONE_LABELS } from './headline';
import { SportFilterRow } from './SportFilter';
import { COMBINED_CHIP, filterLabel } from './sportFilterModel';
import filterStyles from './sportFilter.module.css';
import styles from './fitness.module.css';

const RANGE_OPTIONS = FITNESS_RANGES.map((value) => ({ value, label: value.toUpperCase() }));
const LEGEND = [['FITNESS', 'fitness'], ['FATIGUE', 'fatigue'], ['FORM', 'form']] as const;
const ZONES: readonly FormZone[] = ['fresh', 'neutral', 'productive', 'overreaching'];

// Under ALL the swatch stacks up to three of the sports drawn (top to bottom as they stack); filtered, one sport colour.
function LoadSwatch({ sport, sports }: { sport: SportFilter; sports: readonly Sport[] }) {
  if (sport !== 'all') return <span className={styles.swatch} data-series="load" style={{ background: SPORT_COLORS[sport] }} aria-hidden="true" />;
  return (
    <span className={styles.sportStack} aria-hidden="true">
      {sports.slice(0, 3).reverse().map((s) => <span key={s} data-sport={s} style={{ background: SPORT_COLORS[s] }} />)}
    </span>
  );
}

function Legend({ planned, sport, sports }: { planned: boolean; sport: SportFilter; sports: readonly Sport[] }) {
  return (
    <ul className={styles.legend} aria-label="Legend">
      {LEGEND.map(([label, key]) => (
        <li key={key}><span className={styles.swatch} data-series={key} aria-hidden="true" />{label}</li>
      ))}
      <li><LoadSwatch sport={sport} sports={sports} />{sport === 'all' ? 'LOAD BY SPORT' : `${filterLabel(sport)} LOAD`}</li>
      {planned && <li><span className={styles.swatch} data-series="planned" aria-hidden="true" />PLANNED</li>}
    </ul>
  );
}

function ZoneKey() {
  return (
    <ul className={styles.zoneKey} style={{ top: PHONE_ZONE_KEY_TOP }} aria-label="Form zones">
      {ZONES.map((zone) => (
        <li key={zone}>
          <span className={styles.zoneSwatch} data-zone={zone} style={{ background: ZONE_BAND[zone] }} aria-hidden="true" />
          {ZONE_LABELS[zone]}
        </li>
      ))}
    </ul>
  );
}

export interface FitnessChartPanelProps {
  days: FitnessResponse['days']; forecast: FitnessDay[]; plannedDays: number; range: FitnessRange; onRange: (range: FitnessRange) => void;
  sport?: SportFilter; sports?: readonly Sport[];
}

export function FitnessChartPanel({ days, forecast, plannedDays, range, onRange, sport = 'all', sports = [] }: FitnessChartPanelProps) {
  const variant = useVariant();
  const [chartWrap, chartWidth] = useElementWidth<HTMLDivElement>();
  // Keyed on the fit, not the width, so a resize only rebuilds the option when the end label comes or goes.
  const endLabel = endLabelFits(days, forecast, variant, chartWidth);
  const option = useMemo(() => fitnessChartOption(days, variant, forecast, endLabel, sport), [days, variant, forecast, endLabel, sport]);
  const end = planEnd(days, forecast);
  const drawn = useMemo(() => loadSports(days), [days]);
  const projection = projectionLine(days, forecast, plannedDays);
  return (
    <section className={styles.panel} aria-labelledby="fitness-chart-title">
      <header className={styles.chartHeader}>
        <div className={styles.chartHeadLeft}>
          <p className={styles.chartEyebrow}>
            <span className="sr-only-phone">PERFORMANCE · </span>{RANGE_LABEL[range]}{end !== null && ` + YOUR PLAN TO ${formatWeekdayDayMonth(end)}`} · EVERY ACTIVITY WITH LOAD
          </p>
          <h2 id="fitness-chart-title" className={styles.chartTitle}>FITNESS / FATIGUE / FORM</h2>
        </div>
        <div className={styles.controls}>
          <div className={styles.peakRow}>
            <span className={styles.peak}>{peakLabel(days)}</span>
            <span className={styles.todayNote}>{todayNote(days)}</span>
          </div>
          <Legend planned={end !== null} sport={sport} sports={drawn} />
          <ToggleTabs label="Range" options={RANGE_OPTIONS} value={range} onChange={onRange} className={styles.rangeTabs} />
        </div>
      </header>
      {variant !== 'phone' && sports.length > 0 && <SportFilterRow sport={sport} sports={sports} phone={false} />}
      {variant === 'phone' && sport !== 'all' && <p className={filterStyles.chip}><Layers size={12} aria-hidden="true" />{COMBINED_CHIP}</p>}
      <div ref={chartWrap} className={styles.chartWrap}>
        <EChart option={option} height={variant === 'phone' ? FITNESS_CHART_HEIGHT_PHONE : FITNESS_CHART_HEIGHT} label="Fitness, fatigue and form with daily load" />
        <ZoneKey />
      </div>
      {projection !== null && <p className={styles.projection}>{projection}</p>}
      <p className={styles.hint}><Pointer size={12} aria-hidden="true" />TOUCH &amp; HOLD FOR ANY DAY&apos;S VALUES</p>
      <FitnessDataTable days={days} sport={sport} />
    </section>
  );
}
